<template>
  <div>
    <NuxtLayout>
      <NuxtPage
        :transition="{
          name: 'page',
          mode: 'out-in'
        }"
      />
    </NuxtLayout>
    <LazyCookieBanner v-if="loadCookieBanner" />
    <LazyClientOnly v-if="config.public.environment === 'preview'">
      <DatoDraftMode />
    </LazyClientOnly>
    <ClientOnly>
      <LazyModalOverlay
        v-if="!store.isLoading"
        :show="isVideoVisible"
        :show-close="!isUserInactive"
        :show-back="false"
        @on-close="hideVideo"
      >
        <VideoPlayer
          ref="playerRef"
          class="w-full"
          :options="{
            controls: true,
            autoplay: true,
            preload: 'metadata',
            sources: [
              {
                src: videoUrl,
                type: 'video/mp4'
              }
            ],
            fill: true,
            bigPlayButton: false,
            controlBar: {
              volumePanel: false,
              playToggle: false,
              fullscreenToggle: false,
              pictureInPictureToggle: false,
              currentTimeDisplay: true,
              remainingTimeDisplay: true
            }
          }"
          @userinactive="onUserInactive"
          @useractive="onUserActive"
        />
      </LazyModalOverlay>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { toHead } from 'vue-datocms'

import type Player from 'video.js/dist/types/player'
import '~/assets/css/transitions.css'
import '~/assets/css/fonts.css'
import { useRoute } from 'vue-router'

// import type { OrganizationSimple } from '@unhead/schema-org'
// import { defineOrganization, useSchemaOrg } from '#imports'
import { useAppStore } from '~/stores/AppStore'

const config = useRuntimeConfig()
const appConfig = useAppConfig()
const store = useAppStore()
const { locale } = useI18n()
const route = useRoute()

store.fetchData(locale.value as SiteLocale)

// Delay loading of cookie banner
const loadCookieBanner = ref(false)
onMounted(() => {
  if (config.public.environment !== 'development') {
    document.addEventListener('contextmenu', (e) => e.preventDefault())
  }

  useLenis()

  useTimeoutFn(() => {
    loadCookieBanner.value = true
  }, 3000)
})

const i18nHead = useLocaleHead()

useHead(() => ({
  htmlAttrs: {
    lang: i18nHead.value.htmlAttrs!.lang
  },
  titleTemplate: (titleChunk) => (titleChunk ? `${titleChunk}` : appConfig.title),
  link: [
    { rel: 'preconnect', href: 'https://www.datocms-assets.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com' }
  ],
  meta: [...(i18nHead.value.meta || [])]
}))

useHead(() => {
  return toHead(store._site?.faviconMetaTags || [])
})

const { isVideoVisible, hideVideo, videoUrl } = useModal()
const isUserInactive = ref(false)
const playerRef = ref<Player | null>(null)

const onUserInactive = () => {
  if (playerRef.value?.player.paused()) {
    return
  }
  isUserInactive.value = true
}

const onUserActive = () => {
  isUserInactive.value = false
}

const { emitterEmit } = useEmitter()

watch(
  () => route.fullPath,
  () => {
    useTimeoutFn(() => {
      emitterEmit('hotspotLeave')
    }, 1000)
  }
)

/*
useSchemaOrg([
  defineOrganization({
    name: 'Trampolin Studio',
    logo: `ADD LOGO URL`,
    address: computed(
      () =>
        ({
          '@type': 'PostalAddress',
          streetAddress: 'Poštna ulica 2',
          postalCode: '2000',
          addressLocality: 'Maribor',
          addressCountry: 'Slovenia'
        }) as OrganizationSimple['address']
    )
  })
])
*/
</script>

<style lang="css">
.page-enter-active,
.page-leave-active {
  transition: opacity 600ms cubic-bezier(0.37, 0, 0.63, 1);
}

.page-enter-active {
  transition-delay: 200ms;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
}
</style>
